import type {
    MatchSummary,
    PaginatedMatchesResponse,
    MatchesPaginationQuery,
    Club,
    UserProfile,
    OpponentTeam,
    LoginResponse,
    RegisterResponse,
    InvitationResponse,
    InvitationLinkPayload,
    InvitationLinkResponse,
    TermsStatus,
    AcceptTermsPayload,
    ManagedMembersResponse,
    MatchExistsResponse,
    BackendTeam,
    TeamCreatePayload,
    TeamUpdatePayload,
    PlayerCreatePayload,
    PlayerUpdatePayload,
    RosterPlayer,
    StatsResponse,
    DvwImportInput,
    UploadDvwResponse,
    GetImportStatusResponse,
    ConfirmImportRequestDto,
    ConfirmDvwResponse,
    MatchImportStatus,
    VideoUploadUrlResponse,
    MatchPlayerVisibilityResponse,
    TeamLogoUploadUrlResponse,
    SeasonsResponse,
    VideoExport,
    VideoExportList,
    VideoExportLogo,
    CreateVideoExportPayload,
    MatchViews,
    MatchViewKind,
    TeamTacticsDto,
} from '../types';
import { TeamConflictError } from '../types';
import { deleteIfPresent, HttpError, request } from './http';
import type { SupportedLanguage } from '../languages';
import { Config } from '@/app/constants/config';
import type { FeatureFlagMap } from '@/lib/featureFlags';
import { fetchAllPages, listQuery } from '@/data/services/listQuery';

interface ErrorBody {
    message?: string;
    existingTeamId?: string;
    existingTeamName?: string;
}

function isErrorBody(value: unknown): value is ErrorBody {
    return typeof value === 'object' && value !== null;
}

function readErrorBody(err: unknown): ErrorBody {
    if (err instanceof HttpError && isErrorBody(err.body)) {
        return err.body;
    }
    return {};
}

const MATCHES_IMPORT_PATH = '/matches/import';

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function isConfirmDvwResponse(value: unknown): value is ConfirmDvwResponse {
    if (!isRecord(value)) return false;
    return value.status === 'PROCESSING' && typeof value.draftId === 'string';
}

const MATCH_IMPORT_STATUS_VALUES: readonly MatchImportStatus[] = [
    'PENDING',
    'PARSING',
    'READY',
    'PROCESSING',
    'CONFIRMED',
    'FAILED',
    'EXPIRED',
];

function isMatchImportStatus(value: unknown): value is MatchImportStatus {
    return typeof value === 'string' && MATCH_IMPORT_STATUS_VALUES.some((s) => s === value);
}

function normalizeUploadDvwResponse(value: unknown): UploadDvwResponse {
    if (!isRecord(value)) {
        throw new Error('Réponse import invalide');
    }
    const draftId =
        typeof value.draftId === 'string' ? value.draftId : typeof value.id === 'string' ? value.id : '';
    if (!draftId || !isMatchImportStatus(value.status)) {
        throw new Error('Réponse import invalide');
    }
    const ownerTeamId = typeof value.ownerTeamId === 'string' ? value.ownerTeamId : undefined;
    const createdFromImport = typeof value.createdFromImport === 'boolean' ? value.createdFromImport : undefined;
    return { draftId, status: value.status, ownerTeamId, createdFromImport };
}

export async function uploadDvw(input: DvwImportInput): Promise<UploadDvwResponse> {
    if (!input.ownerTeamId && !input.newOwnTeamName) {
        throw new Error('Sélectionnez une équipe ou renseignez le nom d’une nouvelle équipe à créer.');
    }
    const fd = new FormData();
    fd.append('file', input.file);
    if (input.ownerTeamId) {
        fd.append('ownerTeamId', input.ownerTeamId);
    }
    if (input.newOwnTeamName) {
        fd.append('newOwnTeamName', input.newOwnTeamName);
    }
    if (input.newOwnTeamLevel) {
        fd.append('newOwnTeamLevel', input.newOwnTeamLevel);
    }
    if (input.newOwnTeamGender) {
        fd.append('newOwnTeamGender', input.newOwnTeamGender);
    }
    if (typeof input.newOwnTeamNumber === 'number') {
        fd.append('newOwnTeamNumber', String(input.newOwnTeamNumber));
    }
    fd.append('matchKind', input.matchKind);
    if (input.encodingOverride) fd.append('encodingOverride', input.encodingOverride);
    if (input.dialectOverride) fd.append('dialect', input.dialectOverride);
    if (input.scoutingFileHomeSide) {
        fd.append('expectedHomeTeamDvwKey', input.scoutingFileHomeSide === 'home' ? 'H' : 'V');
    }
    const raw = await request<unknown>(MATCHES_IMPORT_PATH, {
        method: 'POST',
        rawBody: fd,
        // Parsing a large file on the server can outlast the default timeout.
        timeoutMs: null,
    });
    return normalizeUploadDvwResponse(raw);
}

export async function fetchImportStatus(draftId: string): Promise<GetImportStatusResponse> {
    const raw = await request<GetImportStatusResponse>(`${MATCHES_IMPORT_PATH}/${draftId}`);
    return {
        ...raw,
        draftId: raw.draftId ?? raw.id ?? draftId,
    };
}

export async function confirmDvw(draftId: string, dto: ConfirmImportRequestDto): Promise<ConfirmDvwResponse> {
    const raw = await request<ConfirmDvwResponse | undefined>(`${MATCHES_IMPORT_PATH}/${draftId}/confirm`, {
        method: 'POST',
        json: dto,
        // The backend persists an import in a transaction allowed up to 120 s.
        timeoutMs: null,
    });
    if (isConfirmDvwResponse(raw)) return raw;
    return { draftId, status: 'PROCESSING' };
}

export const api = {
    async login(email: string, password: string): Promise<LoginResponse> {
        return request<LoginResponse>('/auth/login', {
            method: 'POST',
            json: { email, password },
        });
    },

    async register(
        email: string,
        password: string,
        name?: string,
        invitationCode?: string,
    ): Promise<RegisterResponse> {
        return request<RegisterResponse>('/auth/register', {
            method: 'POST',
            json: { email, password, name, invitationCode },
        });
    },

    async logout(): Promise<void> {
        await request<void>('/auth/logout', { method: 'POST' });
    },

    async createInvitation(role: string): Promise<InvitationResponse> {
        return request<InvitationResponse>('/users/invite', {
            method: 'POST',
            json: { role },
        });
    },

    async createInvitationLink(data: InvitationLinkPayload): Promise<InvitationLinkResponse> {
        return request<InvitationLinkResponse>('/users/invite-link', {
            method: 'POST',
            json: data,
        });
    },

    async getManagedMembers(): Promise<ManagedMembersResponse> {
        return request<ManagedMembersResponse>('/users/members');
    },

    async addMemberToTeam(userId: string, teamId: string): Promise<void> {
        await request<void>(`/users/members/${encodeURIComponent(userId)}/teams/${encodeURIComponent(teamId)}`, {
            method: 'POST',
        });
    },

    async removeMemberFromTeam(userId: string, teamId: string): Promise<void> {
        await request<void>(`/users/members/${encodeURIComponent(userId)}/teams/${encodeURIComponent(teamId)}`, {
            method: 'DELETE',
        });
    },

    async linkMemberPlayer(userId: string, teamId: string, playerId: string): Promise<void> {
        await request<void>(
            `/users/members/${encodeURIComponent(userId)}/teams/${encodeURIComponent(teamId)}/player`,
            { method: 'PUT', json: { playerId } },
        );
    },

    async unlinkMemberPlayer(userId: string, teamId: string): Promise<void> {
        await request<void>(
            `/users/members/${encodeURIComponent(userId)}/teams/${encodeURIComponent(teamId)}/player`,
            { method: 'DELETE' },
        );
    },

    /** Player under 15 already registered: the club holds a parent's consent, or the statement is withdrawn (VOL-326). */
    async setMemberMinorConsent(userId: string, minorParentalConsent: boolean): Promise<void> {
        await request<void>(`/users/members/${encodeURIComponent(userId)}/minor-consent`, {
            method: 'PUT',
            json: { minorParentalConsent },
        });
    },

    async removeMemberFromClub(userId: string): Promise<void> {
        await request<void>(`/users/members/${encodeURIComponent(userId)}/club`, {
            method: 'DELETE',
        });
    },

    async forgotPassword(email: string): Promise<void> {
        await request<void>('/auth/forgot-password', {
            method: 'POST',
            json: { email },
        });
    },

    async resetPassword(token: string, newPassword: string): Promise<void> {
        await request<void>('/auth/reset-password', {
            method: 'POST',
            json: { token, newPassword },
        });
    },

    /**
     * Change le mot de passe de l'utilisateur connecté.
     *
     * Le mot de passe actuel est exigé par le serveur. Un mot de passe actuel
     * erroné remonte en **403** (et non 401) : un 401 serait interprété par le
     * client HTTP comme une session expirée et déconnecterait l'utilisateur.
     */
    async changePassword(currentPassword: string, newPassword: string): Promise<void> {
        await request<{ message: string; user: unknown }>('/auth/change-password', {
            method: 'POST',
            json: { currentPassword, newPassword },
        });
    },

    /** Renomme le club de l'utilisateur (propriétaire ou Admin Club). */
    async updateOwnClub(data: { name: string; city?: string }): Promise<Club> {
        return request<Club>('/users/club', {
            method: 'PUT',
            json: data,
        });
    },

    async getProfile(): Promise<UserProfile> {
        return request<UserProfile>('/users/profile');
    },

    async updateProfile(data: Partial<UserProfile>): Promise<UserProfile> {
        return request<UserProfile>('/users/profile', {
            method: 'PUT',
            json: data,
        });
    },

    /** Language the emails go out in (VOL-326, VOL-341). */
    async updateLanguage(language: SupportedLanguage): Promise<void> {
        await request<void>('/users/profile/language', { method: 'PUT', json: { language } });
    },

    /** CGU / CGV the account must accept in their current version before going on (VOL-199). */
    async getTermsStatus(): Promise<TermsStatus> {
        return request<TermsStatus>('/users/terms');
    },

    async acceptTerms(payload: AcceptTermsPayload): Promise<TermsStatus> {
        return request<TermsStatus>('/users/terms', { method: 'POST', json: payload });
    },

    /** GDPR art. 17: anonymizes the account; the password is asked again. */
    async deleteAccount(password: string): Promise<void> {
        await request<void>('/users/me', { method: 'DELETE', json: { password } });
    },

    /** GDPR art. 20: everything the account holds, as JSON. */
    async exportAccountData(): Promise<unknown> {
        return request<unknown>('/users/me/export');
    },

    async searchClubs(query: string): Promise<Club[]> {
        return request<Club[]>(`/users/clubs/search?q=${encodeURIComponent(query)}`);
    },

    async checkMatchExists(date: string, opponent: string): Promise<MatchExistsResponse> {
        const params = new URLSearchParams({ date, opponent });
        try {
            return await request<MatchExistsResponse>(`/matches/check?${params.toString()}`);
        } catch (err) {
            if (err instanceof HttpError) {
                return { exists: false };
            }
            throw err;
        }
    },

    async saveMatch(matchData: unknown): Promise<MatchSummary> {
        return request<MatchSummary>('/matches', {
            method: 'POST',
            json: matchData,
            idempotent: true,
        });
    },

    async getSeasons(): Promise<SeasonsResponse> {
        return request<SeasonsResponse>('/seasons');
    },

    async getGlobalStats(params: {
        matchId?: string;
        matchIds?: string[];
        teamId?: string;
        opponentTeamId?: string;
        kind?: 'OWN' | 'SCOUTING';
        season?: string;
        championship?: string;
    } = {}): Promise<StatsResponse> {
        const search = new URLSearchParams();
        if (params.matchId) search.set('matchId', params.matchId);
        if (params.matchIds?.length) search.set('matchIds', params.matchIds.join(','));
        if (params.teamId) search.set('teamId', params.teamId);
        if (params.opponentTeamId) search.set('opponentTeamId', params.opponentTeamId);
        if (params.kind) search.set('kind', params.kind);
        if (params.season) search.set('season', params.season);
        if (params.championship) search.set('championship', params.championship);
        const url = `/matches/stats${search.toString() ? `?${search.toString()}` : ''}`;
        return request<StatsResponse>(url);
    },

    /**
     * Récupère la liste paginée des matchs et retourne UNIQUEMENT la page de
     * données (`data`) pour préserver l'API publique "tableau" consommée par
     * les hooks TanStack Query et les écrans.
     *
     * Le backend garantit le contrat `PaginatedMatchesDto = { data, meta }`
     * (cf. Volley_Stats_Backend/src/features/match/dto/match-response.dto.ts).
     * Aucun fallback "tableau brut" : le contrat est unique.
     *
     * Pour accéder à la pagination (total/page/limit/…), utiliser
     * `getMatchesPage(query)` qui expose la réponse complète.
     */
    async getAllMatches(query?: MatchesPaginationQuery): Promise<MatchSummary[]> {
        const response = await api.getMatchesPage(query);
        return response.data;
    },

    /**
     * Version paginée typée de `/matches` : retourne la réponse complète
     * `{ data, meta }` pour les écrans qui ont besoin d'afficher la
     * pagination (total, page courante, etc.).
     */
    async getMatchesPage(query?: MatchesPaginationQuery): Promise<PaginatedMatchesResponse> {
        const params = new URLSearchParams();
        if (query?.page !== undefined) params.set('page', String(query.page));
        if (query?.limit !== undefined) params.set('limit', String(query.limit));
        if (query?.season) params.set('season', query.season);
        const suffix = params.toString() ? `?${params.toString()}` : '';
        return request<PaginatedMatchesResponse>(`/matches${suffix}`);
    },

    async getMatch(id: string): Promise<MatchSummary> {
        return request<MatchSummary>(`/matches/${id}`);
    },

    async updateMatch(id: string, matchData: unknown): Promise<MatchSummary> {
        // Sets the given fields and upserts the sets: sending it again changes nothing more.
        return request<MatchSummary>(`/matches/${id}`, {
            method: 'PUT',
            json: matchData,
            replayable: true,
        });
    },

    async updateMatchVideo(
        id: string,
        payload: { videoUrl?: string; videoStartOffsetMs?: number },
    ): Promise<MatchSummary> {
        return request<MatchSummary>(`/matches/${id}/video`, {
            method: 'PATCH',
            json: payload,
        });
    },

    async updateMatchPlayerVisibility(
        id: string,
        playerVisibility: boolean | null,
    ): Promise<MatchPlayerVisibilityResponse> {
        return request<MatchPlayerVisibilityResponse>(`/matches/${id}/player-visibility`, {
            method: 'PATCH',
            json: { playerVisibility },
        });
    },

    async createVideoUploadUrl(
        id: string,
        payload: { contentType: string; fileSizeBytes: number },
    ): Promise<VideoUploadUrlResponse> {
        return request<VideoUploadUrlResponse>(`/matches/${id}/video/upload-url`, {
            method: 'POST',
            json: payload,
        });
    },

    /** Signed URL that saves the stored match video instead of playing it (VOL-332). */
    async createVideoDownloadUrl(id: string): Promise<{ downloadUrl: string; expiresInSeconds: number }> {
        return request<{ downloadUrl: string; expiresInSeconds: number }>(`/matches/${id}/video/download-url`);
    },

    async deleteMatch(id: string): Promise<void> {
        await deleteIfPresent(`/matches/${id}`);
    },

    async getTeams(season?: string): Promise<BackendTeam[]> {
        const suffix = season ? `?season=${encodeURIComponent(season)}` : '';
        return request<BackendTeam[]>(`/teams${suffix}`);
    },

    async getTeam(id: string): Promise<BackendTeam> {
        return request<BackendTeam>(`/teams/${id}`);
    },

    async createTeam(data: TeamCreatePayload): Promise<BackendTeam> {
        try {
            return await request<BackendTeam>('/teams', {
                method: 'POST',
                json: data,
            });
        } catch (err) {
            const body = readErrorBody(err);
            if (
                err instanceof HttpError &&
                err.status === 409 &&
                typeof body.existingTeamId === 'string'
            ) {
                throw new TeamConflictError(
                    body.message ?? 'Une équipe avec ce nom existe déjà',
                    String(body.existingTeamId),
                    body.existingTeamName ?? '',
                );
            }
            throw err;
        }
    },

    /** Copies a team (roster, staff, settings, no matches) into a later season (VOL-199). */
    async carryOverTeam(id: string, season?: string): Promise<BackendTeam> {
        return request<BackendTeam>(`/teams/${id}/carry-over`, {
            method: 'POST',
            json: season ? { season } : {},
        });
    },

    async updateTeam(
        id: string,
        data: TeamUpdatePayload | { roster: RosterPlayer[]; teamTactics?: TeamUpdatePayload['teamTactics'] },
    ): Promise<BackendTeam> {
        return request<BackendTeam>(`/teams/${id}`, {
            method: 'PUT',
            json: data,
            timeoutMs: Config.api.teamSaveTimeout,
            replayable: true,
        });
    },

    async addPlayer(teamId: string, player: PlayerCreatePayload): Promise<RosterPlayer> {
        return request<RosterPlayer>(`/teams/${teamId}/players`, {
            method: 'POST',
            json: player,
        });
    },

    async updatePlayer(
        teamId: string,
        playerId: string,
        data: PlayerUpdatePayload,
    ): Promise<RosterPlayer> {
        return request<RosterPlayer>(`/teams/${teamId}/players/${playerId}`, {
            method: 'PUT',
            json: data,
        });
    },

    async listVideoExports(matchId: string): Promise<VideoExportList> {
        return request<VideoExportList>(`/matches/${matchId}/video-exports`);
    },

    async createVideoExport(matchId: string, payload: CreateVideoExportPayload): Promise<VideoExport> {
        return request<VideoExport>(`/matches/${matchId}/video-exports`, {
            method: 'POST',
            json: payload,
            idempotent: true,
        });
    },

    async deleteVideoExport(matchId: string, exportId: string): Promise<void> {
        await deleteIfPresent(`/matches/${matchId}/video-exports/${exportId}`);
    },

    async listActionFavorites(matchId: string): Promise<{ actionIds: string[] }> {
        return request<{ actionIds: string[] }>(`/matches/${matchId}/action-favorites`);
    },

    async setActionFavorite(matchId: string, actionId: string, favorite: boolean): Promise<void> {
        await request<void>(`/matches/${matchId}/action-favorites/${actionId}`, {
            method: favorite ? 'PUT' : 'DELETE',
        });
    },

    async recordMatchView(matchId: string, kind: MatchViewKind): Promise<void> {
        await request<void>(`/matches/${matchId}/views`, { method: 'POST', json: { kind } });
    },

    async recordVideoExportView(matchId: string, exportId: string): Promise<void> {
        await request<void>(`/matches/${matchId}/video-exports/${exportId}/views`, { method: 'POST' });
    },

    async getMatchViews(matchId: string): Promise<MatchViews> {
        return request<MatchViews>(`/matches/${matchId}/views`);
    },

    async getFeatureFlags(): Promise<FeatureFlagMap> {
        const { flags } = await request<{ flags: FeatureFlagMap }>('/feature-flags');
        return flags;
    },

    async listVideoExportLogos(matchId: string): Promise<VideoExportLogo[]> {
        return request<VideoExportLogo[]>(`/matches/${matchId}/video-exports/logos`);
    },

    async createVideoExportLogoUploadUrl(
        matchId: string,
        payload: { contentType: string; fileSizeBytes: number },
    ): Promise<TeamLogoUploadUrlResponse> {
        return request<TeamLogoUploadUrlResponse>(`/matches/${matchId}/video-exports/logos/upload-url`, {
            method: 'POST',
            json: payload,
        });
    },

    async registerVideoExportLogo(matchId: string, key: string): Promise<VideoExportLogo> {
        return request<VideoExportLogo>(`/matches/${matchId}/video-exports/logos`, {
            method: 'POST',
            json: { key },
        });
    },

    async createTeamLogoUploadUrl(
        teamId: string,
        payload: { contentType: string; fileSizeBytes: number },
    ): Promise<TeamLogoUploadUrlResponse> {
        return request<TeamLogoUploadUrlResponse>(`/teams/${teamId}/logo/upload-url`, {
            method: 'POST',
            json: payload,
        });
    },

    async deleteTeam(teamId: string): Promise<void> {
        await request<void>(`/teams/${teamId}`, { method: 'DELETE' });
    },

    async deletePlayer(teamId: string, playerId: string): Promise<void> {
        await request<void>(`/teams/${teamId}/players/${playerId}`, {
            method: 'DELETE',
        });
    },

    async getClubs(): Promise<Club[]> {
        return request<Club[]>('/teams/clubs');
    },

    async getOpponentTeams(season?: string): Promise<OpponentTeam[]> {
        return fetchAllPages((page) => request<OpponentTeam[]>(`/opponent-teams?${listQuery(season, page)}`));
    },

    async getOpponentTeam(id: string): Promise<OpponentTeam> {
        return request<OpponentTeam>(`/opponent-teams/${id}`);
    },

    async createOpponentTeam(data: {
        name: string;
        teamId?: string;
        gender?: string;
        season?: string;
        players: { name?: string; number: number; role?: string }[];
    }): Promise<OpponentTeam> {
        return request<OpponentTeam>('/opponent-teams', {
            method: 'POST',
            json: data,
            idempotent: true,
        });
    },

    async updateOpponentTeam(
        id: string,
        data: {
            name?: string;
            gender?: string;
            jerseyColor?: string | null;
            jerseyColorAway?: string | null;
            players?: { id?: string; name?: string; number: number; role?: string }[];
            removedPlayerIds?: string[];
            teamTactics?: TeamTacticsDto;
        },
    ): Promise<OpponentTeam> {
        return request<OpponentTeam>(`/opponent-teams/${id}`, {
            method: 'PUT',
            json: data,
        });
    },

    /** Moves a record's actions onto another record of the team, then deletes it (VOL-329). */
    async mergeOpponentPlayer(teamId: string, playerId: string, intoPlayerId: string): Promise<OpponentTeam> {
        return request<OpponentTeam>(`/opponent-teams/${teamId}/players/${playerId}/merge`, {
            method: 'POST',
            json: { intoPlayerId },
        });
    },

    async deleteOpponentTeam(id: string): Promise<void> {
        await request<void>(`/opponent-teams/${id}`, { method: 'DELETE' });
    },

    uploadDvw,
    fetchImportStatus,
    confirmDvw,
};
